import { auth } from './auth';
import {
  getDownloadURL,
  getStorageInstance,
  ref,
  uploadBytes,
  uploadBytesResumable,
} from './storage';

// Browser + Node ≥19 native UUID generator. Used instead of `nanoid`
// because `@ps/functions` doesn't depend on it — `crypto.randomUUID`
// is built into every runtime this lib targets.
const uniqueId = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  // Defensive fallback for environments without `crypto.randomUUID`
  // (very old browsers). Not cryptographically strong, but adequate
  // for filename uniqueness.
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export interface UploadUserFileOptions {
  /**
   * Storage prefix the file lands under, scoped to the signed-in
   * user: `${directoryPath}/${uid}/...`. Defaults to `'users'` —
   * the safe, generic location covered by Storage rules for
   * arbitrary user-owned uploads. Use `'digitalProducts'` for
   * digital-product deliverables, which have their own rules path.
   */
  directoryPath?: string;
  /**
   * Optional filename hint prepended to the unique id so the file is
   * easier to identify when browsing Storage manually. The unique
   * `nanoid()` segment is always added regardless — overwriting a
   * stable name would keep CDN-cached previous content alive.
   */
  suffix?: string;
  /**
   * Override the file extension. Defaults to: explicit `ext` →
   * `File.name`'s extension → derived from `metadata.contentType` /
   * `file.type` → `'bin'`.
   */
  ext?: string;
  /** Storage metadata sent with the upload. */
  metadata?: {
    contentType?: string;
    contentDisposition?: string;
  };
  /**
   * Optional progress callback (0-1). When provided, uses
   * `uploadBytesResumable` so progress events can stream; otherwise
   * uses the simpler `uploadBytes`.
   */
  onProgress?: (fraction: number) => void;
}

/**
 * Upload a Blob/File to Firebase Storage under the signed-in user's
 * scoped prefix and return the public download URL. Mirrors RN's
 * `appComponents/file-uploader/uploadFileAsync` so the same patterns
 * work across both apps:
 *
 *   • Throws when no user is signed in.
 *   • Stores at `${directoryPath}/${uid}/${suffix}${nanoid()}.${ext}`
 *     so each upload gets a unique URL — important to avoid CDN-cached
 *     stale content when replacing an existing file (e.g. an avatar).
 *   • Returns the download URL ready to be stored on a Firestore doc.
 */
export async function uploadUserFile(
  file: Blob | File,
  options: UploadUserFileOptions = {},
): Promise<string> {
  const uid = auth()?.currentUser?.uid;
  if (!uid) throw new Error('Not signed in');

  // Extension resolution chain.
  let ext = options.ext;
  if (!ext && file instanceof File) {
    ext = file.name.split('.').pop()?.toLowerCase();
  }
  if (!ext) {
    const contentType =
      options.metadata?.contentType || (file as { type?: string }).type || '';
    ext = contentType.split('/').pop() || 'bin';
  }

  const directoryPath = (options.directoryPath ?? 'users').replace(/\/+$/, '');
  const suffix = options.suffix ? `${options.suffix}-` : '';
  const path = `${directoryPath}/${uid}/${suffix}${uniqueId()}.${ext}`;
  const storageRef = ref(getStorageInstance(), path);

  // Build metadata — explicit contentType wins; fall back to the
  // Blob/File's MIME if the caller didn't override.
  const metadata: { contentType?: string; contentDisposition?: string } = {};
  const contentType =
    options.metadata?.contentType || (file as { type?: string }).type;
  if (contentType) metadata.contentType = contentType;
  if (options.metadata?.contentDisposition) {
    metadata.contentDisposition = options.metadata.contentDisposition;
  }

  if (options.onProgress) {
    // Resumable so we can stream progress events. The caller cares
    // about UI feedback for large uploads (PDF lead magnets, etc.).
    const task = uploadBytesResumable(storageRef, file, metadata);
    task.on('state_changed', snapshot => {
      const fraction =
        snapshot.totalBytes > 0
          ? snapshot.bytesTransferred / snapshot.totalBytes
          : 0;
      options.onProgress?.(fraction);
    });
    await task;
  } else {
    await uploadBytes(storageRef, file, metadata);
  }

  return getDownloadURL(storageRef);
}
